import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";

async function clearStaleOfflineState() {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations();

    if (!registrations?.length) return;

    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(
        cacheKeys
          .filter((key) => /workbox|precache|runtime|pwa/i.test(key))
          .map((key) => caches.delete(key)),
      );
    }

    if (window.location.pathname.endsWith("/offline.html")) {
      const recoveryUrl = new URL(window.location.href);
      recoveryUrl.pathname = "/";
      recoveryUrl.searchParams.set("recovered", Date.now().toString());
      window.location.replace(recoveryUrl.toString());
    }
  } catch {
    // Ignore cleanup failures and continue booting the app.
  }
}

void clearStaleOfflineState();

const rootEl = document.getElementById("root")!;
const app = (
  <HelmetProvider>
    <App />
  </HelmetProvider>
);

// Prerendered marketing pages hydrate so the static HTML stays on screen while
// the route chunk loads. Auth/consent-dependent bits may differ from the build
// snapshot; React recovers by client-rendering those subtrees.
if (rootEl.dataset.prerendered && rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, app, {
    onRecoverableError: (err) => {
      if (import.meta.env.DEV) console.warn("[hydrate]", err);
    },
  });
} else {
  createRoot(rootEl).render(app);
}
